import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AnimationSpeed, ANIMATION_SPEEDS } from '@/types/codeInjection';
import { Zap, Clock, Turtle } from 'lucide-react';

interface AnimationSpeedSelectorProps {
  value: AnimationSpeed;
  onChange: (speed: AnimationSpeed) => void;
}

const SPEED_OPTIONS = [
  {
    value: 'slow' as AnimationSpeed,
    label: 'Slow',
    description: '0.5s transitions',
    icon: Turtle,
    color: 'text-blue-500'
  },
  {
    value: 'normal' as AnimationSpeed,
    label: 'Normal',
    description: '0.3s transitions',
    icon: Clock,
    color: 'text-green-500'
  },
  {
    value: 'fast' as AnimationSpeed,
    label: 'Fast',
    description: '0.15s transitions',
    icon: Zap,
    color: 'text-orange-500'
  }
];

/**
 * AnimationSpeedSelector - A radio group for selecting animation speed
 */
const AnimationSpeedSelector: React.FC<AnimationSpeedSelectorProps> = ({ 
  value, 
  onChange 
}) => {
  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Animation Speed</Label>
      
      <RadioGroup 
        value={value} 
        onValueChange={onChange}
        className="space-y-2"
      >
        {SPEED_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = value === option.value;
          
          return (
            <div key={option.value} className="flex items-center space-x-3">
              <RadioGroupItem 
                value={option.value} 
                id={`speed-${option.value}`}
                className="text-primary"
              />
              <Label 
                htmlFor={`speed-${option.value}`} 
                className={`flex items-center gap-2 cursor-pointer flex-1 p-2 rounded-md border transition-colors ${
                  isSelected 
                    ? 'bg-primary/5 border-primary/20' 
                    : 'hover:bg-gray-50 border-gray-200'
                }`}
              >
                <Icon className={`h-4 w-4 ${option.color}`} />
                <div className="flex-1">
                  <div className="font-medium text-sm">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </div>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
};

export default AnimationSpeedSelector;